// src/App.jsx

import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";

import { ProtectedRoute } from "./components/ProtectedRoute";

import { FeatureFlagRoute } from "./components/FeatureFlagRoute";
import { FEATURES, SKIP_AUTH } from "./lib/config";

import { RootLayout } from "./layouts/RootLayout";
import { Toaster } from "sonner";
import { useDarkMode } from "./hooks/useDarkMode";
import { LoadingProvider } from "./contexts/LoadingContext";
import { PageLoader } from "./components/PageLoader";
import { ScrollToTop } from "./components/ScrollToTop";

// 1. Defina TODOS os componentes de página como "lazy"
const Home = lazy(() =>
  import("./pages/Home").then((module) => ({ default: module.Home })),
);
const Condominos = lazy(() =>
  import("./pages/Condominos").then((module) => ({
    default: module.Condominos,
  })),
);
const AutorizacaoAcesso = lazy(() =>
  import("./pages/AutorizacaoAcesso").then((module) => ({
    default: module.AutorizacaoAcesso,
  })),
);
const Encomendas = lazy(() =>
  import("./pages/Encomendas").then((module) => ({
    default: module.Encomendas,
  })),
);
const EncomendaDetalhesPage = lazy(
  () => import("./pages/EncomendaDetalhesPage"),
);
const HistoricoEncomendasMoradorPage = lazy(
  () => import("./pages/HistoricoEncomendasMoradorPage"),
);
const DashboardPage = lazy(() => import("./pages/DashboardPage"));

const AutorizacaoFormPage = lazy(() => import("./pages/AutorizacaoFormPage"));
const AutorizacaoDetalhesPage = lazy(
  () => import("./pages/AutorizacaoDetalhesPage"),
);
const AcompanhamentoEncomendaPage = lazy(
  () => import("./pages/AcompanhamentoEncomendaPage"),
);
const GerenciarUnidadePage = lazy(() => import("./pages/GerenciarUnidadePage"));
const ConfirmacaoRetiradaPage = lazy(
  () => import("./pages/ConfirmacaoRetiradaPage"),
);
const AssinaturaPage = lazy(() => import("./pages/AssinaturaPage"));
const SyncContatosPage = lazy(() => import("./pages/SyncContatosPage"));
const ConciliacaoWhatsappPage = lazy(
  () => import("./pages/ConciliacaoWhatsappPage"),
);
const WhatsappConexaoPage = lazy(() =>
  import("./pages/WhatsappConexaoPage").then((m) => ({ default: m.WhatsappConexaoPage })),
);

const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const BlacklistPage = lazy(() =>
  import("./pages/BlacklistPage").then((module) => ({
    default: module.BlacklistPage,
  }))
);
const AdminContasPage = lazy(() => import("./pages/AdminContasPage"));
const VisitorRegisterPage = lazy(() => import("./pages/VisitorRegisterPage"));

function App() {
  useDarkMode();

  return (
    <ErrorBoundary>
      <Router>
        <LoadingProvider>
          <ScrollToTop />
          <Toaster richColors closeButton position="top-right" />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />

              <Route
                path="/"
                element={
                  // Se SKIP_AUTH for true, não aplique a proteção.
                  SKIP_AUTH ? (
                    <RootLayout />
                  ) : (
                    <ProtectedRoute>
                      <RootLayout />
                    </ProtectedRoute>
                  )
                }
              >
                <Route path="/" element={<Home />} />
                <Route path="/condominos" element={<Condominos />} />
                <Route
                  path="/condominos/:codigoUnidade/gerenciar"
                  element={<GerenciarUnidadePage />}
                />
                <Route
                  path="/autorizacao-acesso"
                  element={
                    <FeatureFlagRoute isEnabled={FEATURES.AUTORIZACAO_ACESSO}>
                      <AutorizacaoAcesso />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="/autorizacao-acesso/novo"
                  element={<AutorizacaoFormPage />}
                />
                <Route
                  path="/autorizacao-acesso/:id"
                  element={<AutorizacaoDetalhesPage />}
                />
                <Route
                  path="/encomendas"
                  element={
                    <FeatureFlagRoute isEnabled={FEATURES.ENCOMENDAS}>
                      <Encomendas />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="/encomendas/retirada-manual/:encomendaId"
                  element={<ConfirmacaoRetiradaPage />}
                />
                <Route
                  path="/encomendas/historico-morador"
                  element={
                    <FeatureFlagRoute isEnabled={FEATURES.ENCOMENDAS}>
                      <HistoricoEncomendasMoradorPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="/encomendas/historico-morador/:moradorId"
                  element={
                    <FeatureFlagRoute isEnabled={FEATURES.ENCOMENDAS}>
                      <HistoricoEncomendasMoradorPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="/encomendas/assinatura"
                  element={<AssinaturaPage />}
                />
                <Route
                  path="/encomendas/:encomendaId"
                  element={<EncomendaDetalhesPage />}
                />

                <Route
                  path="/blacklist"
                  element={
                    <FeatureFlagRoute isEnabled={FEATURES.BLACKLIST}>
                      <BlacklistPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/sync-contatos" element={<SyncContatosPage />} />
                <Route path="/admin/contas" element={<AdminContasPage />} />
                <Route path="/admin/whatsapp" element={<WhatsappConexaoPage />} />
                <Route
                  path="/admin/conciliacao-whatsapp"
                  element={
                    <FeatureFlagRoute isEnabled={FEATURES.WHATSAPP_CONCILIACAO}>
                      <ConciliacaoWhatsappPage />
                    </FeatureFlagRoute>
                  }
                />
              </Route>

              <Route
                path="/encomenda/acompanhar/:token"
                element={<AcompanhamentoEncomendaPage />}
              />
              <Route
                path="/register"
                element={<VisitorRegisterPage />}
              />
              <Route path="*" element={<div>Página não encontrada</div>} />
            </Routes>
          </Suspense>
        </LoadingProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
